import React, { useEffect, useState } from 'react';
import { useHistory, Link } from "react-router-dom";
import './style.css';
import { useSelector, useDispatch } from 'react-redux';
import { loginUser, authSelector, clearState } from './authSlice';
import { setLink } from '../../redux/commonReduxSlice';
import toast, { Toaster, ToastBar } from 'react-hot-toast';
import useWindowDimensions from '../../config/useWindowDimensions';
import { SyncOutlined, EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { Layout, Form, Input, Button, Row, Col, Spin, Image } from 'antd';

const { Content } = Layout;

const antIcon = <SyncOutlined style={{ fontSize: 32, color: '#ff4500' }} spin />;

const Login = () => {
  const dispatch = useDispatch();
  let history = useHistory();
  const [form] = Form.useForm();
  const [isUserName, setUserName] = useState('');
  const { height } = useWindowDimensions();
  const [isLoading, setLoading] = useState(false);

  const { isError, isSuccess, errorMessage, setPassKey } = useSelector(authSelector);

  useEffect(() => {
    return () => {
      dispatch(clearState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (isError) {
      dispatch(clearState());
      setLoading(false);
      if (setPassKey && errorMessage === "Kindly create a new password") {
        history.push({
          pathname: '/reset-password',
          state: { setPassKey: setPassKey, username: isUserName }
        });
      } else {
        toast.error(errorMessage);
      }
    }
    if (isSuccess) {
      dispatch(clearState());
      setLoading(false);
      dispatch(setLink(null, null));
      toast.success('Logged in successfully');
      history.push('/dashboard');
    }
  }, [isError, isSuccess, setLoading, dispatch, errorMessage, history, isUserName, setPassKey]);

  const onFinish = (values) => {
    setUserName(values.username);
    let data = {
      "username": values.username.toLowerCase(),
      "password": values.password
    };
    setLoading(true);
    dispatch(loginUser(data));
  };

  // Modern UI Styles
  const styles = {
    layout: { background: '#fff', minHeight: '100vh' },
    leftCol: {
      background: `linear-gradient(rgba(0,0,0,0.2), rgba(0,0,0,0.2)), url('./images/login-bg.png') center center / cover`,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh'
    },
    rightCol: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      padding: '0 10%',
      height: '100vh',
      backgroundColor: '#fdfdfd'
    },
    headerText: {
      color: '#172B4D',
      fontSize: '36px',
      fontWeight: '700',
      lineHeight: '1.2',
      marginBottom: '10px'
    },
    subHeaderText: {
      color: '#6B778C',
      fontSize: '18px',
      fontWeight: '400',
      marginBottom: '40px'
    },
    inputField: {
      height: '50px',
      borderRadius: '8px',
      fontSize: '16px',
      border: '2px solid #DFE1E6'
    },
    loginBtn: {
      height: '55px',
      borderRadius: '8px',
      fontSize: '18px',
      fontWeight: '600',
      backgroundColor: '#ff4500',
      borderColor: '#ff4500',
      boxShadow: '0 4px 14px 0 rgba(255,69,0,0.39)',
      marginTop: '20px'
    },
    loadingOverlay: {
      position: 'absolute',
      zIndex: 1000,
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'rgba(255,255,255,0.7)',
      backdropFilter: 'blur(2px)'
    }
  };

  return (
    <Layout style={styles.layout}>
      <Content>
        <div style={{ position: 'relative', height: '100vh', overflow: 'hidden' }}>
          
          {isLoading && (
            <div style={styles.loadingOverlay}>
              <Spin indicator={antIcon} />
            </div>
          )}

          <Toaster
            toastOptions={{
              style: {
                borderRadius: '10px',
                background: '#333',
                color: '#fff',
                padding: '16px',
              },
            }}
          >
            {(t) => (
              <ToastBar toast={t}>
                {({ icon, message }) => (
                  <>
                    {icon}
                    {message}
                    {t.type !== 'loading' && (
                      <button 
                        onClick={() => toast.dismiss(t.id)}
                        style={{ background: 'transparent', border: 'none', color: '#fff', cursor: 'pointer', marginLeft: '8px' }}
                      >✕</button>
                    )}
                  </>
                )}
              </ToastBar>
            )}
          </Toaster>

          <Row>
            {/* Left Side: Branding */}
            <Col xs={0} sm={0} md={9} style={styles.leftCol}>
              <Image
                preview={false}
                width={200}
                src="./images/logo3.png"
                style={{ filter: 'drop-shadow(0px 4px 10px rgba(0,0,0,0.3))' }}
              />
            </Col>

            {/* Right Side: Form */}
            <Col xs={24} sm={24} md={15} style={styles.rightCol}>
              <div style={{ maxWidth: '450px', width: '100%', margin: '0 auto' }}>
                {/* <div style={{ marginBottom: '8px', color: '#ff4500', fontWeight: '600', letterSpacing: '1px' }}>
                  BPMS ENTERPRISE. inq. inc. EDMS Solution
                </div> */}
                <h1 style={styles.headerText}>Business Process Automation System</h1>
                <p style={styles.subHeaderText}>Welcome back to BPMS, <strong>inq. inc EDMS Solution!</strong> Please enter your credentials to access the dashboard.</p>

                <Form
                  form={form}
                  layout="vertical"
                  onFinish={onFinish}
                  requiredMark={false}
                >
                  <Form.Item
                    label={<span style={{ fontWeight: '600', color: '#444' }}>Appointment</span>}
                    name="username"
                    rules={[{ required: true, message: 'Please enter your appointment!' }]}
                  >
                    <Input 
                      placeholder="Enter Your Appointment" 
                      style={styles.inputField} 
                      autoComplete="off"
                    />
                  </Form.Item>

                  <Form.Item
                    label={<span style={{ fontWeight: '600', color: '#444' }}>Password</span>}
                    name="password"
                    rules={[{ required: true, message: 'Please enter your password!' }]}
                    style={{ marginTop: '20px' }}
                  >
                    <Input.Password
                      placeholder="Enter your password"
                      style={styles.inputField}
                      iconRender={visible => (visible ? <EyeTwoTone twoToneColor="#ff4500" /> : <EyeInvisibleOutlined />)}
                    />
                  </Form.Item>

                  <Form.Item style={{ marginTop: '40px' }}>
                    <Button 
                      type="primary" 
                      htmlType="submit" 
                      block 
                      style={styles.loginBtn}
                      loading={isLoading}
                    >
                      Login to System
                    </Button>
                  </Form.Item>

                  <div style={{ textAlign: 'center', marginTop: '24px' }}>
                    {/* <Link to="/forgot-password" style={{ color: '#6B778C', fontSize: '14px', fontWeight: '500' }}>
                      Forgot Password?
                    </Link> */}
                  </div>
                </Form>
              </div>
            </Col>
          </Row>
        </div>
      </Content>
    </Layout>
  );
};

export default Login;